import { PROJECT_MAX_DURATION, TIMELINE_START_BOUNDARY } from 'consts'
import { TAspectRatio } from 'store/types/aiImage'
import { IUserAudio } from 'store/types/audio'
import { IAudioItem } from 'types/audio'
import { IMediaItem, MediaStatus } from 'types/media'
import { IProjectData } from 'types/projectData'
import { ITtsItemData, IUpdatedTtsItemData } from 'types/text-to-speech'
import { ITimelineItem } from 'types/timeline'
import { isBlobUrl } from 'utils'
import { logError } from 'utils/logger'
import { convertTtsItemIntoAudioItem } from 'utils/media'
import { secondsToPixels } from 'utils/timeline'
const SECONDS_IDENTIFIER = '_second'

export const filterExistingAudios = (
  audios: IUpdatedTtsItemData[],
  multilineAudios: IAudioItem[]
): IUpdatedTtsItemData[] => {
  return audios.filter(audio =>
    multilineAudios.some(
      existingAudio => `${audio.originalId}` === existingAudio.assetId
    )
  )
}

export const getNewAudioItems = (
  audios: ITtsItemData[],
  multilineAudios: IAudioItem[]
): ITtsItemData[] => {
  return audios.filter(
    audio =>
      !multilineAudios.some(
        existingAudio => `${audio.id}` === existingAudio.assetId
      )
  )
}

export const mutateExistingAudioItemsInPlace = (
  updatedAudios: IUpdatedTtsItemData[],
  multilineAudios: IAudioItem[]
) => {
  const targetMultilineAudios = multilineAudios.filter(multilineAudio =>
    updatedAudios.find(
      audio => `${audio.originalId}` === multilineAudio.assetId
    )
  )
  targetMultilineAudios.forEach(targetMultilineAudio => {
    const audioSource = updatedAudios.find(
      audio => `${audio.originalId}` === targetMultilineAudio.assetId
    )
    if (!audioSource) {
      logError(
        `Expected to find audio source for ${targetMultilineAudio.id} with assetId: ${targetMultilineAudio.assetId}, but wasn't able to.`
      )
      return
    }
    targetMultilineAudio.duration = audioSource.duration
    targetMultilineAudio.mediaDuration = audioSource.duration
    targetMultilineAudio.mediaStart = 0
    targetMultilineAudio.mediaPath = audioSource.filePath
    targetMultilineAudio.title = audioSource.text
    targetMultilineAudio.assetId = `${audioSource.id}`
    targetMultilineAudio.id = crypto.randomUUID()
  })
}

export const shiftOverlappedTimelineItems = <T extends { id: string }>({
  //all the timeline items are expected to have an id
  itemsArray,
  itemStartKey,
  itemDurationKey,
  processedItemId
}: {
  itemsArray: T[]
  itemStartKey: keyof T
  itemDurationKey: keyof T
  processedItemId?: string
}): void => {
  if (itemsArray.length === 0) return
  const sortedItems = sortArraysByKey(itemStartKey, itemsArray)

  const updatedItemIndex = processedItemId
    ? sortedItems.findIndex(item => item.id === processedItemId)
    : 0

  if (updatedItemIndex === -1 || updatedItemIndex + 1 === itemsArray.length) {
    return
  }

  let itemEndTime =
    (sortedItems[updatedItemIndex][itemStartKey] as number) +
    (sortedItems[updatedItemIndex][itemDurationKey] as number)

  for (let i = updatedItemIndex + 1; i < sortedItems.length; i++) {
    const nextItem = sortedItems[i]
    const nextItemOriginalStart = nextItem[itemStartKey] as number
    const nextItemDuration = nextItem[itemDurationKey] as number
    if (nextItemOriginalStart >= itemEndTime && processedItemId) {
      // if the processed item id is provided and there is no collision,
      // we can  break the loop as the data is sorted
      break
    }

    const adjustedStart = Math.max(nextItemOriginalStart, itemEndTime)
    ;(nextItem[itemStartKey] as number) = adjustedStart
    itemEndTime = adjustedStart + nextItemDuration
  }
}

export const getNewAudioItemsWithAdjustedPosition = (
  regeneratedTtsItems: IUpdatedTtsItemData[],
  ttsMultilineAudios: IAudioItem[],
  multilineAudios: IAudioItem[]
): IAudioItem[] => {
  const newTtsAudios = getNewAudioItems(regeneratedTtsItems, ttsMultilineAudios)

  const newAudioItems = newTtsAudios.map(convertTtsItemIntoAudioItem)

  const firstLineAudios = multilineAudios.filter(audio => audio.lineIndex === 0)
  const sortedFirstLineAudios = sortArraysByKey('projectStart', firstLineAudios)

  const lastAudio = sortedFirstLineAudios[sortedFirstLineAudios.length - 1]
  let lastAudioEnd = lastAudio ? lastAudio.projectStart + lastAudio.duration : 0

  for (let i = 0; i < newAudioItems.length; i++) {
    const currentAudio = newAudioItems[i]
    currentAudio.projectStart = lastAudioEnd
    lastAudioEnd += currentAudio.duration
  }
  return newAudioItems
}

/**
 * Creates a duplicated item and positions it at the end of the line
 * @param items - Array of items to search through
 * @param targetId - ID of the item to duplicate
 * @param compareField - Field to use for comparing items (zIndex for media, lineIndex for audio)
 * @param positionField - Field to use for positioning (startTime for media, projectStart for audio)
 * @returns The duplicated item or null if original item not found
 */
export const createDuplicatedItem = <T extends IMediaItem | IAudioItem>(
  items: T[],
  targetId: string,
  compareField: keyof T,
  positionField: keyof T
): T | null => {
  const originalItem = items.find(item => item.id === targetId)
  if (!originalItem) return null

  const lastItemOnLine = items.reduce<T | null>((latest, current) => {
    const isSameLine = current[compareField] === originalItem[compareField]
    if (!isSameLine) return latest

    if (!latest) return current

    const latestEnd = Number(latest[positionField]) + latest.duration
    const currentEnd = Number(current[positionField]) + current.duration

    return currentEnd > latestEnd ? current : latest
  }, null)

  if (!lastItemOnLine) return null

  return {
    ...originalItem,
    id: crypto.randomUUID(),
    [positionField]:
      Number(lastItemOnLine[positionField]) + lastItemOnLine.duration
  }
}

export const findAudioById = (
  audios: IAudioItem[],
  audioId: string
): { audio: IAudioItem; index: number } | null => {
  const index = audios.findIndex(a => a.id === audioId)
  if (index === -1) return null
  return { audio: audios[index], index }
}

export const updateAudioWithVersion = (
  audio: IAudioItem,
  version: IUserAudio
): void => {
  audio.assetId = version.id.toString()
  audio.mediaPath = version.path
  audio.title = version.title
  audio.duration = version.duration
  audio.mediaDuration = version.duration
  audio.mediaStart = 0
}
export const resolveCollisionsAfterDurationIncrease = (
  audios: IAudioItem[],
  updatedAudio: IAudioItem,
  oldDuration: number
): void => {
  const newDuration = updatedAudio.duration
  // Only handle collisions if the new duration is longer
  if (newDuration <= oldDuration) return

  // Get audios in the same line
  const currentLineAudios = audios
    .filter(a => a.lineIndex === updatedAudio.lineIndex)
    .sort((a, b) => a.projectStart - b.projectStart)

  // Find the index of the updated audio in its line
  const indexInLine = currentLineAudios.findIndex(a => a.id === updatedAudio.id)
  if (indexInLine === -1) return

  // Calculate where the updated clip now ends
  let cursor = updatedAudio.projectStart + newDuration

  // Walk through subsequent clips and push any that collide
  for (let i = indexInLine + 1; i < currentLineAudios.length; i++) {
    const next = currentLineAudios[i]

    // Check if this clip starts before our cursor (collision)
    if (next.projectStart < cursor) {
      const overlap = cursor - next.projectStart
      next.projectStart += overlap // Push it forward
      cursor = next.projectStart + next.duration // Advance cursor past this clip
    } else {
      // No overlap means everything beyond is already clear
      break
    }
  }
}

export const sortArraysByKey = <T>(key: keyof T, ...arrays: T[][]): T[] => {
  const combined = arrays.flat()
  if (combined.length === 0 || !(key in (combined[0] as object))) {
    return combined
  }
  return combined.sort((a, b) => (a[key] as number) - (b[key] as number))
}

export const getCurrentLineItems = <T extends { lineIndex: number }>(
  items: T[],
  currentLineIndex: number | undefined
): T[] => items.filter(item => item.lineIndex === currentLineIndex)

export const findActiveItemsBounds = (
  timelineItems: ITimelineItem[],
  timelineItem: ITimelineItem,
  timelineRulerSliceDuration: number,
  activeTimelineItemIds?: string[]
) => {
  const currentLineItems = sortArraysByKey(
    'startTime',
    getCurrentLineItems(timelineItems, timelineItem.lineIndex)
  )

  const itemIndex = currentLineItems.findIndex(
    media => media.id === timelineItem.id
  )
  const leftSibling = currentLineItems[itemIndex - 1]
  const rightSibling = currentLineItems[itemIndex + 1]

  // Check if sibling is also an active item
  const isLeftSiblingActive =
    leftSibling && activeTimelineItemIds?.includes(leftSibling.id)
  const isRightSiblingActive =
    rightSibling && activeTimelineItemIds?.includes(rightSibling.id)

  const leftSiblingEndTime = secondsToPixels(
    leftSibling && !isLeftSiblingActive
      ? leftSibling.startTime + leftSibling.duration
      : TIMELINE_START_BOUNDARY,
    timelineRulerSliceDuration
  )

  const rightSiblingStartTime = secondsToPixels(
    rightSibling && !isRightSiblingActive
      ? rightSibling.startTime
      : PROJECT_MAX_DURATION,
    timelineRulerSliceDuration
  )

  const left = secondsToPixels(
    timelineItem.startTime,
    timelineRulerSliceDuration
  )
  const right = secondsToPixels(
    timelineItem.startTime + timelineItem.duration,
    timelineRulerSliceDuration
  )

  const leftBoundary = leftSiblingEndTime - left
  const rightBoundary = rightSiblingStartTime - right

  return { left, leftBoundary, rightBoundary, right }
}
const greatestCommonDivisor = (a: number, b: number): number => {
  if (b === 0) return a
  return greatestCommonDivisor(b, a % b)
}

export const convertSizeToAspectRatio = (
  width: number,
  height: number
): TAspectRatio => {
  const gcd = greatestCommonDivisor(width, height)
  return `${width / gcd}:${height / gcd}`
}

export const generateBreakpoints = (
  items: ITimelineItem[],
  timelineRulerSliceDuration: number
) => {
  const timelineBoundaryBreakpoints = [
    TIMELINE_START_BOUNDARY,
    PROJECT_MAX_DURATION
  ].map(boundary => secondsToPixels(boundary, timelineRulerSliceDuration))

  const breakpoints = items
    .flatMap(item => [
      secondsToPixels(item.startTime, timelineRulerSliceDuration), // Start time in pixels
      secondsToPixels(
        item.startTime + item.duration,
        timelineRulerSliceDuration
      ) // End time in pixels
    ])
    .filter((value, index, array) => array.indexOf(value) === index) // Remove duplicates

  return [...timelineBoundaryBreakpoints, ...breakpoints]
}

const mergeMediaItem = (
  mediaItem: IMediaItem,
  updatedItem: Partial<IMediaItem>
) => {
  const objectFields = Object.entries(mediaItem)
    .filter(([_key, value]) => typeof value === 'object' && value !== null)
    .map(([key, value]) => [
      key,
      {
        ...value,
        ...(updatedItem
          ? (updatedItem[key as keyof typeof updatedItem] as typeof value)
          : {})
      }
    ])

  const objectFieldsObject = Object.fromEntries(objectFields)

  return {
    ...mediaItem,
    ...updatedItem,
    ...objectFieldsObject
  }
}

// Reusable function to update project data arrays
export const updateProjectDataArray = (
  projectDataArray: IProjectData[],
  payload: {
    id: string
    updates: Partial<IMediaItem>
    status: MediaStatus
  }[]
) => {
  const mediaItemsIds = payload.map(item => item.id)
  return projectDataArray.map(projectData => ({
    ...projectData,
    mediaItems: projectData.mediaItems.map(mediaItem => {
      if (
        (mediaItemsIds.includes(mediaItem.id) &&
          mediaItem.meta.status === MediaStatus.PENDING) ||
        isBlobUrl(mediaItem.meta.path)
      ) {
        const updatedItem = payload.find(
          updatedItem => updatedItem.id === mediaItem.id
        )

        const updatedMediaItem = mergeMediaItem(
          mediaItem,
          updatedItem?.updates as Partial<IMediaItem>
        )
        return updatedMediaItem
      }

      return mediaItem
    })
  }))
}

export const getPendingMediaItems = (
  mediaItemsArray: Array<{ mediaItems: IMediaItem[] }>
) => {
  return mediaItemsArray
    .map(item => item.mediaItems)
    .flat()
    .filter(item => item.meta.status === MediaStatus.PENDING)
}

export const getIsLibrarySound = (path: string) =>
  path.toLocaleLowerCase().includes(SECONDS_IDENTIFIER)

export const getNumericUserSoundIds = (sounds: IAudioItem[]) => {
  return sounds.reduce<number[]>((ids, sound) => {
    if (!getIsLibrarySound(sound.mediaPath)) {
      ids.push(Number(sound.assetId))
    }
    return ids
  }, [])
}
